import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import React, { createContext, useState, useMemo, useContext, useEffect } from 'react';
import './styles/tokens.css';
import { glass, glassInteractive, paletteTokens } from './styles/glass';

type Mode = 'light' | 'dark';

interface ColorModeContextType {
  toggleColorMode: () => void;
  mode: Mode;
}

const ColorModeContext = createContext<ColorModeContextType>({
  toggleColorMode: () => { },
  mode: 'dark',
});

export const useColorMode = () => useContext(ColorModeContext);

export const ThemeContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<Mode>(() => {
    const saved = localStorage.getItem('themeMode');
    if (saved === 'light' || saved === 'dark') return saved;
    return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  });

  // Reason: tokens.css resolves every colour off this attribute, so stamping it
  // on <html> is what actually switches the theme. MUI's palette below only
  // needs literals for its own contrast maths.
  //
  // This runs during RENDER rather than in an effect, and that is the whole
  // point: React runs CHILD effects before PARENT effects. While this lived in
  // a useEffect, any child reacting to `mode` ran while <html> still carried
  // the PREVIOUS theme.
  //
  // That is what broke the embedded Swagger UI. APIDocsPage reads the resolved
  // token VALUES out of the host document (the iframe cannot inherit them) and
  // bakes them into the iframe stylesheet as literals. On a flip to light it
  // ran first, read the still-dark tokens, and wrote a dark stylesheet — then
  // this effect turned the app light. Nothing re-injected afterwards, so a
  // light app kept a dark Swagger until a reload.
  //
  // Stamping during render makes "the DOM reflects `mode`" true before any
  // consumer effect can observe it, which fixes it for every future reader of
  // these tokens rather than only for Swagger. The write is idempotent, so a
  // StrictMode double-render is harmless.
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', mode);
  }

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prev) => {
          const next = prev === 'light' ? 'dark' : 'light';
          localStorage.setItem('themeMode', next);
          return next;
        }),
      mode,
    }),
    [mode],
  );

  const theme = useMemo(() => {
    const t = paletteTokens[mode];

    return createTheme({
      palette: {
        mode,
        primary: { main: t.inflow, contrastText: mode === 'dark' ? '#062018' : '#ffffff' },
        secondary: { main: t.outflow, contrastText: '#ffffff' },
        info: { main: t.invest },
        warning: { main: t.warn },
        error: { main: t.danger },
        success: { main: t.inflow },
        background: { default: t.bg, paper: t.paper },
        text: { primary: t.text, secondary: t.textDim },
        divider: mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(20,36,31,0.12)',
      },
      typography: {
        fontFamily: 'Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        h1: { fontSize: '2.75rem', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.025em' },
        h2: { fontSize: '2rem', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.02em' },
        h3: { fontSize: '1.5rem', fontWeight: 650, lineHeight: 1.25, letterSpacing: '-0.015em' },
        h4: { fontSize: '1.25rem', fontWeight: 650, lineHeight: 1.3 },
        h5: { fontSize: '1.0625rem', fontWeight: 600 },
        h6: { fontSize: '0.9375rem', fontWeight: 650 },
        // Section kickers: small, wide-tracked, uppercase.
        overline: { fontSize: '0.6875rem', fontWeight: 750, letterSpacing: '0.14em', lineHeight: 1.6 },
        body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
        body2: { fontSize: '0.8125rem', lineHeight: 1.55 },
        caption: { fontSize: '0.75rem', lineHeight: 1.5 },
        button: { fontWeight: 600, textTransform: 'none', letterSpacing: 0 },
      },
      shape: { borderRadius: 14 },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            'html, body': { minHeight: '100%' },
            '#root': { minHeight: '100vh' },
            '*': {
              scrollbarWidth: 'thin',
              scrollbarColor: 'var(--scrollbar-thumb) transparent',
            },
            '*::-webkit-scrollbar': { width: 8, height: 8 },
            '*::-webkit-scrollbar-track': { background: 'transparent' },
            '*::-webkit-scrollbar-thumb': {
              background: 'var(--scrollbar-thumb)',
              borderRadius: 'var(--radius-pill)',
            },
            '*::-webkit-scrollbar-thumb:hover': { background: 'var(--scrollbar-thumb-hover)' },
            '@media (prefers-reduced-motion: reduce)': {
              '*, *::before, *::after': {
                animationDuration: '0.01ms !important',
                animationIterationCount: '1 !important',
                transitionDuration: '0.01ms !important',
              },
            },
          },
        },

        MuiButton: {
          defaultProps: { disableElevation: true },
          styleOverrides: {
            root: {
              borderRadius: 'var(--radius-md)',
              minHeight: 40,
              padding: '8px 16px',
              cursor: 'pointer',
              transition: 'all var(--dur-base) var(--ease-out)',
              '&:focus-visible': { outline: '2px solid var(--accent)', outlineOffset: 2 },
            },
            contained: {
              background: 'var(--accent)',
              color: 'var(--on-accent)',
              '&:hover': { background: 'var(--accent-hover)', transform: 'translateY(-1px)' },
            },
            outlined: {
              background: 'var(--glass-2)',
              borderColor: 'var(--border)',
              color: 'var(--text)',
              backdropFilter: 'blur(var(--glass-blur))',
              WebkitBackdropFilter: 'blur(var(--glass-blur))',
              '&:hover': { background: 'var(--glass-hover)', borderColor: 'var(--border-strong)' },
            },
            text: { '&:hover': { background: 'var(--glass-2)' } },
          },
        },

        // Cards are the main panel. One sheet of glass, no nested boxes.
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 'var(--radius-lg)',
              overflow: 'hidden',
              ...glass({ level: 2 }),
            },
          },
        },
        MuiCardContent: {
          styleOverrides: { root: { padding: 18, '&:last-child': { paddingBottom: 18 } } },
        },

        MuiPaper: {
          styleOverrides: {
            root: { backgroundImage: 'none' },
            elevation0: { background: 'transparent', backdropFilter: 'none', boxShadow: 'none' },
            elevation1: { ...glass({ level: 1 }) },
            elevation2: { ...glass({ level: 2 }) },
            elevation3: { ...glass({ level: 3, tier: 'overlay' }) },
          },
        },

        MuiAppBar: {
          styleOverrides: {
            root: {
              ...glass({ tier: 'chrome', level: 2, border: false, sheen: false }),
              borderBottom: '1px solid var(--border)',
              color: 'var(--text)',
            },
          },
        },

        MuiDrawer: {
          styleOverrides: {
            paper: {
              ...glass({ tier: 'chrome', level: 1, border: false, sheen: false }),
              borderRight: '1px solid var(--border)',
            },
          },
        },

        MuiDialog: {
          styleOverrides: {
            paper: { borderRadius: 'var(--radius-xl)', ...glass({ tier: 'overlay', level: 3 }) },
          },
        },
        MuiBackdrop: {
          styleOverrides: {
            root: {
              backgroundColor: 'rgba(4, 8, 13, 0.55)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
            },
          },
        },
        MuiMenu: {
          styleOverrides: { paper: { ...glass({ tier: 'overlay', level: 3 }) } },
        },
        MuiPopover: {
          styleOverrides: { paper: { ...glass({ tier: 'overlay', level: 3 }) } },
        },
        MuiTooltip: {
          styleOverrides: {
            tooltip: {
              ...glass({ tier: 'overlay', level: 3 }),
              color: 'var(--text)',
              fontSize: '0.75rem',
              padding: '6px 10px',
            },
          },
        },

        // Tables: hairline rows on a transparent ground, no boxed cells.
        MuiTableContainer: { styleOverrides: { root: { background: 'transparent' } } },
        MuiTableCell: {
          styleOverrides: {
            root: {
              borderBottom: '1px solid var(--border-soft)',
              padding: '10px 14px',
              fontSize: '0.8125rem',
            },
            head: {
              background: 'transparent',
              color: 'var(--text-faint)',
              fontSize: '0.6875rem',
              fontWeight: 750,
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
              borderBottom: '1px solid var(--border)',
            },
          },
        },
        MuiTableRow: {
          styleOverrides: {
            root: {
              transition: 'background-color var(--dur-fast) var(--ease-out)',
              '&:hover': { background: 'var(--glass-1)' },
              '&:last-child td': { borderBottom: 0 },
            },
          },
        },

        MuiChip: {
          styleOverrides: {
            root: {
              borderRadius: 'var(--radius-pill)',
              fontWeight: 600,
              fontSize: '0.6875rem',
              height: 22,
              background: 'var(--glass-3)',
              border: '1px solid var(--border-soft)',
            },
          },
        },

        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              borderRadius: 'var(--radius-md)',
              background: 'var(--glass-1)',
              '& fieldset': { borderColor: 'var(--border)' },
              '&:hover fieldset': { borderColor: 'var(--border-strong)' },
            },
            input: { fontSize: '0.875rem' },
          },
        },

        MuiIconButton: {
          styleOverrides: {
            root: {
              cursor: 'pointer',
              borderRadius: 'var(--radius-md)',
              '&:hover': { background: 'var(--glass-2)' },
              '&:focus-visible': { outline: '2px solid var(--accent)', outlineOffset: 2 },
            },
          },
        },

        MuiDivider: { styleOverrides: { root: { borderColor: 'var(--border-soft)' } } },
        MuiListItemButton: {
          styleOverrides: {
            root: {
              borderRadius: 'var(--radius-md)',
              ...glassInteractive(),
              '&:hover': { background: 'var(--glass-2)' },
              '&.Mui-selected': {
                background: 'var(--accent-soft)',
                color: 'var(--accent-text)',
                '&:hover': { background: 'var(--accent-soft)' },
              },
            },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8125rem',
              minHeight: 40,
              cursor: 'pointer',
            },
          },
        },
        MuiLinearProgress: {
          styleOverrides: {
            root: { height: 6, borderRadius: 'var(--radius-pill)', background: 'var(--glass-3)' },
          },
        },
      },
    });
  }, [mode]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};
